/* eslint-disable max-lines */
import dayjs from 'dayjs';
import HTMLReactParser from 'html-react-parser';
import purifier from 'dompurify';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/elements/Button';
import Card from '../../components/elements/Card';
import DataTable from '../../components/elements/DataTable';
import { ModalDownloadOutOfRange } from '../../components/elements/ModalDownload';
import LoadingBars from '../../components/elements/LoadingBars/LoadingBars';
import ScrollToTop from '../../components/elements/ScrollToTop';
import Text from '../../components/fields/Text';
import IconFilterWithCircle from '../../components/icons/IconFilterWithCircle';
import ModalSetPassword from '../../components/fragments/ModalSetPassword';
import { AppContext } from '../../contexts';
import { useDebounce } from '../../utils/hooks';
import AuditFilterSelect from './AuditFilterSelect';
import DateFilterField from './DateFilterField';
import {
  fetchAuditLog,
  fetchAuditLogActions,
  fetchAuditLogEmails,
  fetchDownload,
  resetAuditLogActionsOptions,
  resetAuditLogEmailsOptions,
  setAuditLogFilter,
} from './actions';
import styles from './styles.scoped.css';

function sanitizeFilterValues(value = []) {
  return value
    .map((item) => (typeof item === 'string' ? item : item?.value))
    .filter(Boolean);
}

function buildOptions(selectedValues = [], fetchedValues = []) {
  const mergedValues = [...selectedValues, ...fetchedValues];
  return mergedValues.reduce((accumulator, current) => {
    if (
      current &&
      !accumulator.find((item) => item.value === current)
    ) {
      accumulator.push({ label: current, value: current });
    }

    return accumulator;
  }, []);
}

function isSameArray(left = [], right = []) {
  return JSON.stringify(left) === JSON.stringify(right);
}

function getDefaultRange() {
  const today = new Date();
  return [today, today];
}

function shouldSearchApi(value = '') {
  return !value || value.length >= 3;
}

export default function AuditLogMerchant({ category }) {
  const { setBreadcrumbs } = useContext(AppContext);
  const dispatch = useDispatch();
  const downloadRef = useRef(true);
  const latestListParamsRef = useRef('');
  const [search, setSearch] = useState('');
  const [range, setRange] = useState(getDefaultRange);
  const [page, setPage] = useState(1);
  const [modalSetPassword, setModalSetPassword] = useState(false);
  const [modalOutRangeVisible, setModalOutRangeVisible] = useState(false);

  const {
    filterAudit: storedFilter,
    dataAudit,
    dataAuditActions,
    dataAuditEmails,
    dataDownload,
    isLoadingDownload,
    isLoadingAudit,
    isLoadingAuditActions,
    isLoadingAuditEmails,
  } = useSelector((state) => state.auditLog);

  const values = {
    search: '',
    startDate: '',
    endDate: '',
    action: [],
    email: [],
    ...storedFilter,
  };

  const { data, meta } = dataAudit;
  const debouncedSearch = useDebounce(search, 500);

  const selectedActionValues = sanitizeFilterValues(values.action);
  const selectedEmailValues = sanitizeFilterValues(values.email);
  const actionFilterData = dataAuditActions?.data || [];
  const emailFilterData = dataAuditEmails?.data || [];
  const actionFilterMeta = dataAuditActions?.meta || {};
  const emailFilterMeta = dataAuditEmails?.meta || {};
  const actionOptions = useMemo(
    () => buildOptions(selectedActionValues, actionFilterData),
    [selectedActionValues, actionFilterData]
  );
  const emailOptions = useMemo(
    () => buildOptions(selectedEmailValues, emailFilterData),
    [selectedEmailValues, emailFilterData]
  );
  const actionHasMore = (actionFilterMeta.page || 0) < (actionFilterMeta.totalPage || 0);
  const emailHasMore = (emailFilterMeta.page || 0) < (emailFilterMeta.totalPage || 0);

  function buildParams(nextPage = 1, nextOverrides = {}) {
    const startDate = dayjs(range[0]).format('YYYY-MM-DD');
    const endDate = dayjs(range[1]).format('YYYY-MM-DD');
    const actionValues = sanitizeFilterValues(
      nextOverrides.action ?? values.action
    );
    const emailValues = sanitizeFilterValues(
      nextOverrides.email ?? values.email
    );
    const params = {
      page: nextPage,
      size: 10,
      search: (nextOverrides.search ?? debouncedSearch ?? '').trim(),
      startDate,
      endDate,
      category,
      ...(actionValues.length
        ? { action: JSON.stringify(actionValues) }
        : {}),
      ...(emailValues.length
        ? { email: JSON.stringify(emailValues) }
        : {}),
    };

    return Object.fromEntries(
      Object.entries(params).filter(([, value]) => value !== '')
    );
  }

  function buildDownloadParams(password) {
    return {
      sbmId: '',
      startDate: dayjs(range[0]).format('YYYY-MM-DD'),
      endDate: dayjs(range[1]).format('YYYY-MM-DD'),
      category,
      search: (debouncedSearch ?? search ?? '').trim(),
      password,
    };
  }

  function fetchListAuditLog(params, options = {}) {
    const { force = false } = options;
    const nextParamsKey = JSON.stringify(params || {});

    if (!force && latestListParamsRef.current === nextParamsKey) {
      return;
    }

    latestListParamsRef.current = nextParamsKey;
    dispatch(fetchAuditLog(params));
  }

  function handleChangeFilter(name, value) {
    dispatch(
      setAuditLogFilter({
        ...values,
        [name]: sanitizeFilterValues(value),
      })
    );
  }

  function buildFilterOptionParams(value = '', nextPage = 1) {
    const params = {
      page: nextPage,
      size: 10,
      search: value.trim(),
    };

    return Object.fromEntries(
      Object.entries(params).filter(([, item]) => item !== '')
    );
  }

  function handleSearchFilter(value, name, nextPage = 1) {
    const nextValue = value?.trim?.() || '';

    if (name === 'action') {
      if (!nextValue) {
        dispatch(resetAuditLogActionsOptions());
        return;
      }

      if (!shouldSearchApi(nextValue)) {
        return;
      }

      dispatch(fetchAuditLogActions(buildFilterOptionParams(nextValue, nextPage)));
    }

    if (name === 'email') {
      if (!nextValue) {
        dispatch(resetAuditLogEmailsOptions());
        return;
      }

      if (!shouldSearchApi(nextValue)) {
        return;
      }

      dispatch(fetchAuditLogEmails(buildFilterOptionParams(nextValue, nextPage)));
    }
  }

  function handleOpenFilter(name) {
    if (name === 'action') {
      dispatch(resetAuditLogActionsOptions());
    }

    if (name === 'email') {
      dispatch(resetAuditLogEmailsOptions());
    }
  }

  function handleFetchMoreFilter(value, name) {
    const nextValue = value?.trim?.() || '';

    if (!shouldSearchApi(nextValue)) {
      return;
    }

    if (name === 'action' && actionHasMore && !isLoadingAuditActions) {
      handleSearchFilter(nextValue, name, (actionFilterMeta.page || 0) + 1);
    }

    if (name === 'email' && emailHasMore && !isLoadingAuditEmails) {
      handleSearchFilter(nextValue, name, (emailFilterMeta.page || 0) + 1);
    }
  }

  const renderActionBadge = (value) => {
    const actionClass = value?.action?.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase() || '';

    return (
      <span
        className={[styles.actionBadge, styles[`color-${actionClass}`]].join(' ')}
      >
        {value.action || '-'}
      </span>
    );
  };

  const column = [
    {
      heading: 'Tanggal',
      value: 'createdAt',
      className: styles.dateColumn,
    },
    {
      heading: 'User',
      value: (value) => (
        <strong className={styles.userName}>{value.username || '-'}</strong>
      ),
      className: styles.userColumn,
    },
    {
      heading: 'Email',
      value: (value) => (
        <span className={styles.emailText}>
          {value.emailAddress || value.email || '-'}
        </span>
      ),
      className: styles.emailColumn,
    },
    {
      heading: 'Tindakan',
      value: (value) => renderActionBadge(value),
      className: styles.actionColumn,
    },
    {
      heading: 'Deskripsi',
      value: (value) => (
        <div className={styles.descriptionCell}>
          {HTMLReactParser(purifier.sanitize(value.description || '-'))}
        </div>
      ),
      className: styles.descriptionColumn,
    },
    {
      heading: 'IP Address',
      value: (value) => (
        <span className={styles.ipText}>{value.ipAddress || '-'}</span>
      ),
      className: styles.ipColumn,
    },
  ];

  useEffect(() => {
    setBreadcrumbs(['Audit Log']);
  }, []);

  useEffect(() => {
    const isRangeValid = Object.values(range).every(Boolean);
    const nextSearch = (debouncedSearch || '').trim();

    if (!isRangeValid || !shouldSearchApi(nextSearch)) {
      return;
    }

    const nextFilter = {
      ...values,
      search: nextSearch,
      startDate: dayjs(range[0]).format('YYYY-MM-DD'),
      endDate: dayjs(range[1]).format('YYYY-MM-DD'),
      action: selectedActionValues,
      email: selectedEmailValues,
    };

    const shouldUpdateFilter =
      values.search !== nextFilter.search ||
      values.startDate !== nextFilter.startDate ||
      values.endDate !== nextFilter.endDate ||
      !isSameArray(selectedActionValues, sanitizeFilterValues(values.action)) ||
      !isSameArray(selectedEmailValues, sanitizeFilterValues(values.email));

    if (shouldUpdateFilter) {
      dispatch(setAuditLogFilter(nextFilter));
    }

    setPage(1);
    fetchListAuditLog(buildParams(1, nextFilter));
  }, [category, debouncedSearch, range, values.action, values.email]);

  useEffect(() => {
    const isRangeValid = Object.values(range).every(Boolean);

    if (page > 1 && isRangeValid) {
      fetchListAuditLog(buildParams(page));
    }
  }, [page]);

  useEffect(() => {
    if (dataDownload?.fileUrl && !isLoadingDownload && !downloadRef.current) {
      setModalSetPassword(false);
      const anchor = document.createElement('a');
      anchor.href = dataDownload.fileUrl;
      anchor.click();
      downloadRef.current = true;
    }
  }, [dataDownload, isLoadingDownload]);

  function handleDownload({ password }) {
    downloadRef.current = false;
    dispatch(
      fetchDownload(buildDownloadParams(password))
    );
  }

  function handleOpenDownload() {
    const startDate = dayjs(range[0]);
    const endDate = dayjs(range[1]);
    const diff = endDate.diff(startDate, 'day');

    if (diff <= 31) {
      setModalSetPassword(true);
      return;
    }

    setModalOutRangeVisible(true);
  }

  function handleResetFilter() {
    setSearch('');
    setRange(getDefaultRange());
    dispatch(resetAuditLogActionsOptions());
    dispatch(resetAuditLogEmailsOptions());
    dispatch(
      setAuditLogFilter({
        ...values,
        search: '',
        startDate: '',
        endDate: '',
        action: [],
        email: [],
      })
    );
  }

  return (
    <div className={styles.root}>
      <Card className={styles.toolBoxFilter}>
        <div className={styles.filterHeader}>
          <div className={styles.filterTitle}>
            <IconFilterWithCircle />
            <span>Filter by:</span>
          </div>
          <Button
            className={styles.resetFilterButton}
            onClick={handleResetFilter}
            outlined
            style="default"
          >
            Reset Filter
          </Button>
        </div>
        <div className={styles.filterFields}>
          <div className={styles.field}>
            <DateFilterField onChange={setRange} range={range} />
          </div>
          <div className={styles.field}>
            <AuditFilterSelect
              data={emailOptions}
              hasMore={emailHasMore}
              label="Email"
              loading={isLoadingAuditEmails}
              name="email"
              onChange={(value) => handleChangeFilter('email', value)}
              onFetchMore={handleFetchMoreFilter}
              onOpen={handleOpenFilter}
              onSearchChange={handleSearchFilter}
              placeholder="Semua Email"
              searchPlaceholder="Cari Email"
              value={selectedEmailValues}
            />
          </div>
          <div className={styles.field}>
            <AuditFilterSelect
              data={actionOptions}
              hasMore={actionHasMore}
              label="Tindakan"
              loading={isLoadingAuditActions}
              name="action"
              onChange={(value) => handleChangeFilter('action', value)}
              onFetchMore={handleFetchMoreFilter}
              onOpen={handleOpenFilter}
              onSearchChange={handleSearchFilter}
              placeholder="Semua Tindakan"
              searchPlaceholder="Cari Tindakan"
              value={selectedActionValues}
            />
          </div>
        </div>
      </Card>
      <Card className={styles.toolBox}>
        <div className={styles.toolBoxAction}>
          <Button
            className={styles.downloadButton}
            disabled={isLoadingDownload}
            onClick={handleOpenDownload}
          >
            {isLoadingDownload ? (
              <LoadingBars className={styles.loadingBars} size={1} />
            ) : (
              <img alt="" aria-hidden="true" src="assets/ic-download.svg" />
            )}
            Unduh Data
          </Button>
        </div>
        <form
          className={[
            styles.searchForm,
            search ? styles.searchFormHasValue : '',
          ].filter(Boolean).join(' ')}
          onSubmit={(event) => event.preventDefault()}
        >
          <Text
            hideLabel
            inputProps={{
              placeholder: 'Cari nama user, email, atau tindakan',
              value: search,
              icon: 'ic-search.svg',
              onChange: (event) => setSearch(event.target.value),
            }}
          />
          {search && (
            <button
              className={styles.searchClearButton}
              onClick={() => setSearch('')}
              type="button"
            >
              Hapus
            </button>
          )}
        </form>
      </Card>
      <Card>
        <div className={styles['table-container']}>
          <DataTable
            className={styles.table}
            column={column}
            data={data}
            endTreshould={500}
            loading={isLoadingAudit}
            meta={meta}
            onEndReached={() => setPage((prevPage) => prevPage + 1)}
            onRefresh={(params) => fetchListAuditLog(params, { force: true })}
            withIndex
          />
        </div>
      </Card>
      <ModalDownloadOutOfRange
        onClose={() => setModalOutRangeVisible(false)}
        open={modalOutRangeVisible}
      />
      <ModalSetPassword
        handledClose={() => setModalSetPassword(false)}
        handledDownload={handleDownload}
        isLoading={isLoadingDownload}
        open={modalSetPassword}
      />
      <ScrollToTop />
    </div>
  );
}

AuditLogMerchant.propTypes = {
  category: PropTypes.string,
};
